import { useMutation } from '@tanstack/react-query';
import { serverRequest } from '../util/server_request';
import { extractErrorMessage } from './extractErrorMessage';
import { notifyUser } from '../util/util';

const notifiedErrors = new Set();

// Helper function to notify only once for the same error
const notifyOnce = (type, message, title = "") => {
  if (!notifiedErrors.has(message)) {
    notifiedErrors.add(message);
    notifyUser(type, message, title);
  }
};

/**
 * Hook to initiate policy report generation
 * Calls POST /policy-reports?policy_number=XXX&year=YYYY
 * Supports single year (string) or multiple years (array)
 * After success, client should call GET /generated-policy-reports
 */
export function usePolicyReports() {
  return useMutation({
    mutationFn: async ({ policyNumber, year = '' }) => {
      try {
        if (!policyNumber) {
          throw new Error('Policy number is required');
        }

        // Build query parameters
        const params = new URLSearchParams();
        params.append('policy_number', policyNumber);
        
        // Handle year parameter
        // year can be: '' (all years), '2024' (single), ['2024', '2025'] (multiple)
        if (Array.isArray(year) && year.length > 0) {
          // Multiple years selected - join with commas
          params.append('year', year.join(','));
        } else if (year && year !== 'all') {
          // Single year selected
          params.append('year', year);
        }
        // If year is empty string or 'all', no year param is sent (backend returns all years)
        
        const endpoint = `policy-reports?${params.toString()}`;
        const response = await serverRequest('POST', endpoint);
        
        // Handle API errors
        if (response?.error) {
          const errorMessages = {
            "You have exceeded your Requests.": {
              message: 'Request limit exceeded. Please try again later.',
              type: 'warning',
              title: 'Rate Limit'
            },
            "Not Found.": {
              message: 'The requested reports could not be found.',
              type: 'error',
              title: 'Not Found'
            }
          };

          const errorConfig = errorMessages[response.error] || {
            message: response.error,
            type: 'error',
            title: 'Error'
          };

          notifyOnce(errorConfig.type, errorConfig.message, errorConfig.title);
          throw new Error(errorConfig.message);
        }

        if (!response?.success) {
          const errorMessage = response?.message || 'Failed to initiate policy report';
          notifyOnce('error', errorMessage, 'Error');
          throw new Error(errorMessage);
        }

        return response;
        
      } catch (error) {
        if (error?.message?.toLowerCase().includes('exceeded your requests')) {
          const limitMessage = 'Request limit exceeded. Please try again later.';
          notifyOnce("warning", limitMessage, "Rate Limit");
          throw new Error(limitMessage);
        }
        
        const errorMessage = extractErrorMessage(error, 'Failed to initiate policy report');
        notifyOnce("error", errorMessage, "Error");
        throw new Error(errorMessage);
      }
    },
  });
}
